import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import FontFamily from "@tiptap/extension-font-family";
import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3, Pilcrow,
  List, ListOrdered, Quote, Minus,
  AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Image as ImageIcon, Unlink,
  Undo2, Redo2, RemoveFormatting, Palette, Highlighter,
  Upload, Code
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLORS = [
  { label: "Marine OFA (défaut)", value: "#14274B" },
  { label: "Noir", value: "#000000" },
  { label: "Bleu OFA", value: "#2D78F6" },
  { label: "Rouge", value: "#DC2626" },
  { label: "Vert", value: "#16A34A" },
  { label: "Orange", value: "#EA580C" },
  { label: "Violet", value: "#7C3AED" },
  { label: "Gris", value: "#6B7280" },
];

function ToolBtn({ icon: Icon, onClick, active, tip, disabled }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`h-8 w-8 ${active ? "bg-accent text-accent-foreground" : ""}`}
          onClick={onClick}
          type="button"
          disabled={disabled}
        >
          <Icon className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom"><p className="text-xs">{tip}</p></TooltipContent>
    </Tooltip>
  );
}

function ToolSep() {
  return <div className="w-px h-6 bg-border mx-0.5" />;
}

const FONTS = [
  { label: "Poppins", value: "Poppins, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times", value: "Times New Roman, serif" },
];

export default function RichTextEditor({ value, onChange, mini = false }) {
  const fileInputRef = useRef(null);
  const isInternalUpdate = useRef(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlSource, setHtmlSource] = useState("");
  const [openPicker, setOpenPicker] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextStyle,
      Color,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-ofa-blue underline" } }),
      Image.configure({ HTMLAttributes: { class: "max-w-full rounded my-4" }, allowBase64: true }),
      Placeholder.configure({ placeholder: mini ? "Résumé accrocheur..." : "Commencez à écrire votre article..." }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      isInternalUpdate.current = true;
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: mini
          ? "min-h-[80px] p-3 focus:outline-none text-sm leading-relaxed prose prose-slate max-w-none"
          : "min-h-[400px] p-4 md:p-6 focus:outline-none text-base leading-relaxed prose prose-slate max-w-none",
        style: "color: #14274B",
        "data-testid": "editor-content",
      },
      handlePaste: (view, event) => {
        // Clean Google Docs paste: remove empty spans, normalize spaces
        const html = event.clipboardData?.getData("text/html");
        if (html && (html.includes("docs-internal") || html.includes("google-docs"))) {
          event.preventDefault();
          let cleaned = html
            .replace(/<span[^>]*style="[^"]*font-weight:\s*400[^"]*"[^>]*>/g, "")
            .replace(/<\/span>/g, "")
            .replace(/<span[^>]*>\s*<\/span>/g, "")
            .replace(/&nbsp;/g, " ")
            .replace(/<b style="font-weight:\s*normal;?">/g, "")
            .replace(/<\/b>/g, "")
            .replace(/\s{2,}/g, " ");
          view.dispatch(view.state.tr.replaceSelection(
            view.state.schema.nodeFromJSON({ type: "doc", content: [] }).type.schema.topNodeType.create()
          ).deleteSelection());
          editor.commands.insertContent(cleaned);
          return true;
        }
        return false;
      },
    },
  });

  // Sync external value changes (e.g. article load)
  useEffect(() => {
    if (editor && value !== undefined && !isInternalUpdate.current) {
      const currentHTML = editor.getHTML();
      if (value !== currentHTML) {
        editor.commands.setContent(value || "", false);
      }
    }
    isInternalUpdate.current = false;
  }, [value, editor]);

  // Ctrl+K / Cmd+K shortcut for links
  useEffect(() => {
    if (!editor) return;
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const url = prompt("URL du lien :");
        if (!url) return;
        if (editor.state.selection.empty) {
          editor.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run();
        } else {
          editor.chain().focus().setLink({ href: url }).run();
        }
      }
    };
    const el = editor.view.dom;
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [editor]);

  const handleImageUpload = () => fileInputRef.current?.click();

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    // Convert to base64 for inline embedding
    const reader = new FileReader();
    reader.onload = () => {
      editor.chain().focus().setImage({ src: reader.result }).run();
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const insertLink = () => {
    const url = prompt("URL du lien :");
    if (!url || !editor) return;
    if (editor.state.selection.empty) {
      editor.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run();
    } else {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const insertImageUrl = () => {
    const url = prompt("URL de l'image :");
    if (url && editor) editor.chain().focus().setImage({ src: url }).run();
  };

  const toggleHtmlMode = useCallback(() => {
    if (!editor) return;
    if (!htmlMode) {
      // Switching TO html mode — grab current content
      setHtmlSource(editor.getHTML());
    } else {
      // Switching BACK to visual — apply html changes
      isInternalUpdate.current = true;
      editor.commands.setContent(htmlSource, false);
      onChange(htmlSource);
    }
    setHtmlMode(!htmlMode);
  }, [editor, htmlMode, htmlSource, onChange]);

  const handleHtmlChange = (e) => {
    setHtmlSource(e.target.value);
  };

  const applyHtml = () => {
    if (!editor) return;
    isInternalUpdate.current = true;
    editor.commands.setContent(htmlSource, false);
    onChange(htmlSource);
    setHtmlMode(false);
  };

  if (!editor) return null;

  return (
    <div className="rich-editor border border-input rounded-md overflow-hidden" data-testid="rich-text-editor" onClick={() => { if (openPicker) setOpenPicker(null); }}>
      <TooltipProvider delayDuration={300}>
        <div className="flex flex-wrap items-center gap-0.5 p-2 bg-muted/50 border-b border-input">
          {/* Undo / Redo */}
          <ToolBtn icon={Undo2} onClick={() => editor.chain().focus().undo().run()} tip="Annuler (Ctrl+Z)" disabled={!editor.can().undo()} />
          <ToolBtn icon={Redo2} onClick={() => editor.chain().focus().redo().run()} tip="Rétablir (Ctrl+Y)" disabled={!editor.can().redo()} />

          <ToolSep />

          {/* Block type */}
          <ToolBtn icon={Pilcrow} onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")} tip="Paragraphe" />
          <ToolBtn icon={Heading1} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} tip="Titre 1" />
          <ToolBtn icon={Heading2} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} tip="Titre 2" />
          <ToolBtn icon={Heading3} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} tip="Titre 3" />

          <ToolSep />

          {/* Inline formatting */}
          <ToolBtn icon={Bold} onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} tip="Gras (Ctrl+B)" />
          <ToolBtn icon={Italic} onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} tip="Italique (Ctrl+I)" />
          <ToolBtn icon={UnderlineIcon} onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} tip="Souligné (Ctrl+U)" />
          <ToolBtn icon={Strikethrough} onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} tip="Barré" />

          <ToolSep />

          {/* Color */}
          <div className="relative">
            <ToolBtn icon={Palette} onClick={() => setOpenPicker(openPicker === "color" ? null : "color")} tip="Couleur du texte" active={openPicker === "color"} />
            {openPicker === "color" && (
              <div className="absolute top-full left-0 mt-0 flex bg-white border border-input rounded-md shadow-lg p-2 gap-1 z-50" onMouseDown={(e) => e.preventDefault()}>
                {COLORS.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    className="w-6 h-6 rounded-full border border-input hover:scale-110 transition-transform"
                    style={{ backgroundColor: c.value }}
                    title={c.label}
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setColor(c.value).run(); setOpenPicker(null); }}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <ToolBtn icon={Highlighter} onClick={() => setOpenPicker(openPicker === "highlight" ? null : "highlight")} tip="Surligner" active={openPicker === "highlight"} />
            {openPicker === "highlight" && (
              <div className="absolute top-full left-0 mt-0 flex bg-white border border-input rounded-md shadow-lg p-2 gap-1 z-50" onMouseDown={(e) => e.preventDefault()}>
                {[{ label: "Jaune", value: "#FEF08A" }, { label: "Vert", value: "#BBF7D0" }, { label: "Bleu", value: "#BFDBFE" }, { label: "Rose", value: "#FBCFE8" }].map(c => (
                  <button
                    key={c.value}
                    type="button"
                    className="w-6 h-6 rounded-full border border-input hover:scale-110 transition-transform"
                    style={{ backgroundColor: c.value }}
                    title={c.label}
                    onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHighlight({ color: c.value }).run(); setOpenPicker(null); }}
                  />
                ))}
                <button
                  type="button"
                  className="w-6 h-6 rounded-full border border-input hover:scale-110 transition-transform flex items-center justify-center text-[10px]"
                  title="Supprimer"
                  onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetHighlight().run(); setOpenPicker(null); }}
                >✕</button>
              </div>
            )}
          </div>

          <ToolSep />

          {/* Font Family */}
          <select
            onChange={(e) => {
              if (e.target.value === "default") {
                editor.chain().focus().unsetFontFamily().run();
              } else {
                editor.chain().focus().setFontFamily(e.target.value).run();
              }
            }}
            className="h-8 px-2 text-xs border border-input rounded bg-background cursor-pointer"
            data-testid="font-family-select"
          >
            {FONTS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>

          {!mini && <ToolSep />}

          {/* Alignment */}
          {!mini && <>
          <ToolBtn icon={AlignLeft} onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} tip="Aligner à gauche" />
          <ToolBtn icon={AlignCenter} onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} tip="Centrer" />
          <ToolBtn icon={AlignRight} onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} tip="Aligner à droite" />

          <ToolSep />

          {/* Lists & blocks */}
          <ToolBtn icon={List} onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} tip="Liste à puces" />
          <ToolBtn icon={ListOrdered} onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} tip="Liste numérotée" />
          <ToolBtn icon={Quote} onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} tip="Citation" />
          <ToolBtn icon={Minus} onClick={() => editor.chain().focus().setHorizontalRule().run()} tip="Ligne horizontale" />

          <ToolSep />

          {/* Links & Images */}
          <ToolBtn icon={LinkIcon} onClick={insertLink} active={editor.isActive("link")} tip="Insérer un lien" />
          {editor.isActive("link") && (
            <ToolBtn icon={Unlink} onClick={() => editor.chain().focus().unsetLink().run()} tip="Supprimer le lien" />
          )}
          <ToolBtn icon={ImageIcon} onClick={insertImageUrl} tip="Image (URL)" />
          <ToolBtn icon={Upload} onClick={handleImageUpload} tip="Importer une image" />

          <ToolSep />

          {/* Clear formatting */}
          <ToolBtn icon={RemoveFormatting} onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} tip="Supprimer le formatage" />

          <ToolSep />

          {/* HTML source toggle */}
          <ToolBtn icon={Code} onClick={toggleHtmlMode} active={htmlMode} tip={htmlMode ? "Retour éditeur visuel" : "Modifier le HTML"} />
          </>}
        </div>
      </TooltipProvider>

      {htmlMode ? (
        <div className="relative" data-testid="html-source-editor">
          <textarea
            value={htmlSource}
            onChange={handleHtmlChange}
            className="w-full min-h-[400px] p-4 md:p-6 font-mono text-sm leading-relaxed bg-slate-950 text-emerald-300 focus:outline-none resize-y"
            spellCheck={false}
            data-testid="html-source-textarea"
          />
          <div className="flex items-center gap-2 p-2 bg-slate-900 border-t border-slate-700">
            <Button
              type="button"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              onClick={applyHtml}
              data-testid="html-apply-btn"
            >
              Appliquer le HTML
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white text-xs"
              onClick={() => { navigator.clipboard.writeText(htmlSource); }}
              data-testid="html-copy-btn"
            >
              Copier le HTML
            </Button>
            <span className="text-xs text-slate-500 ml-auto">Mode HTML — collez ou modifiez le code source</span>
          </div>
        </div>
      ) : (
        <EditorContent editor={editor} />
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFileSelected}
        data-testid="editor-image-upload"
      />
    </div>
  );
}
